import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import kittyAngel from '../DailyDigest/Badges/kitty_angel.png';
import kittyAstronaut from '../DailyDigest/Badges/kitty_astronaut.png';
import kittyCelebrate from '../DailyDigest/Badges/kitty_celebrate.png';
import kittyCoffee from '../DailyDigest/Badges/kitty_coffee.png';
import kittyConfuse from '../DailyDigest/Badges/kitty_confuse.png';
import kittyCool from '../DailyDigest/Badges/kitty_cool.png';
import kittyDetective from '../DailyDigest/Badges/kitty_detective.png';
import kittyExcite from '../DailyDigest/Badges/kitty_excite.png';
import kittyFlower from '../DailyDigest/Badges/kitty_flower.png';
import kittyIdea from '../DailyDigest/Badges/kitty_idea.png';
import kittyMusic from '../DailyDigest/Badges/kitty_music.png';
import kittyPopsicle from '../DailyDigest/Badges/kitty_popsicle.png';
import kittyRead from '../DailyDigest/Badges/kitty_read.png';
import kittyRobot from '../DailyDigest/Badges/kitty_robot.png';
import kittyScientist from '../DailyDigest/Badges/kitty_scientist.png';
import kittySleep from '../DailyDigest/Badges/kitty_sleep.png';
import kittySweating from '../DailyDigest/Badges/kitty_sweating.png';
import kittyWriting from '../DailyDigest/Badges/kitty_writing.png';
import kittyZombie from '../DailyDigest/Badges/kitty_zombie.png';

// Map of image filenames to imported images
const badgeImages = {
  'kitty_angel.png': kittyAngel,
  'kitty_astronaut.png': kittyAstronaut,
  'kitty_celebrate.png': kittyCelebrate,
  'kitty_coffee.png': kittyCoffee,
  'kitty_confuse.png': kittyConfuse,
  'kitty_cool.png': kittyCool,
  'kitty_detective.png': kittyDetective,
  'kitty_excite.png': kittyExcite,
  'kitty_flower.png': kittyFlower,
  'kitty_idea.png': kittyIdea,
  'kitty_music.png': kittyMusic,
  'kitty_popsicle.png': kittyPopsicle,
  'kitty_read.png': kittyRead,
  'kitty_robot.png': kittyRobot,
  'kitty_scientist.png': kittyScientist,
  'kitty_sleep.png': kittySleep,
  'kitty_sweating.png': kittySweating,
  'kitty_writing.png': kittyWriting,
  'kitty_zombie.png': kittyZombie,
};

const AchievementModal = ({ achievement, onClose, onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { setIsAchievementModalVisible } = useContext(GlobalContext);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (achievement) {
      console.log('Achievement data:', achievement);
      console.log('Image URL:', achievement.imageUrl);
      setIsVisible(true);
      setIsAchievementModalVisible(true);
      // Mark the achievement as complete
      handleComplete();
    }
  }, [achievement]);

  const handleComplete = async () => {
    if (!achievement) return;

    try {
      const userId = localStorage.getItem('userID');

      await axios.put(
        `/api/v1/achievement/user/${userId}/achievement/${achievement.id}?achieved=true`
      );

      // Call the onComplete callback if provided
      if (onComplete) {
        onComplete(achievement.id);
      }
    } catch (error) {
      console.error('Error updating achievement:', error.response?.data?.detail || error.message);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setIsAchievementModalVisible(false);
    // Wait for fade out animation before calling onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!achievement) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-90 flex items-center justify-center"
            onClick={handleClose}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-xs" />

            {/* Achievement Content */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 25,
              }}
              className="relative z-10 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-white text-shadow-lg mb-6"
              >
                <h2 className="text-4xl font-bold mb-2">🎉 Achievement Unlocked! 🎉</h2>
                <p className="text-2xl">{achievement.name}</p>
              </motion.div>

              <motion.div
                animate={{
                  y: [0, -20, 0],
                  rotate: [0, -5, 5, -5, 0],
                }}
                transition={{
                  y: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                  },
                  rotate: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                  },
                }}
                className="w-64 h-64 mx-auto mb-8"
              >
                <img
                  src={badgeImages[achievement.imageUrl] || badgeImages['kitty_confuse.png']}
                  alt={achievement.alt}
                  className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-white text-shadow-lg"
              >
                <p className="text-xl mb-8">{achievement.description}</p>
                <button
                  onClick={handleClose}
                  className="px-8 py-3 text-lg bg-white/20 hover:bg-white/30 rounded-full border-2 border-white text-white transition-all duration-300 hover:scale-105"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Move Confetti after the modal div and increase its z-index */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <Confetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={200}
              gravity={0.3}
            />
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AchievementModal;
